import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { SELECTORS } from '../../constants/selectors';

const HIDDEN_CLASS = 'tw:hidden';
const FLEX_CLASS = 'tw:flex';

const LABEL_APPLIED = '取得済み';

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * クーポンモーダルコンポーネント
 *
 * @element kd-coupon-modal
 * @description クーポンコード取得モーダルの開閉を制御する。
 *   HTML は Liquid（v2-product-coupon-modal）が生成し、本コンポーネントは
 *   開閉操作・フォーカストラップを担う。kd-coupon-get がモーダル内の
 *   コードコピー・取得操作を担う。
 *
 *   モーダルを開くトリガーは本要素の外側（document 内のどこか）にある
 *   `[data-coupon-trigger]` 要素のクリックを document レベルで監視する。
 *   閉じる経路は: ESC キー / オーバーレイクリック / `[data-coupon-modal-close]` クリック。
 */
@customElement('kd-coupon-modal')
export class KdCouponModal extends LitElement {
  createRenderRoot() {
    return this;
  }

  private previouslyFocusedElement: HTMLElement | null = null;

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.querySelector(SELECTORS.COUPON_MODAL.OVERLAY)?.addEventListener(
      'click',
      this.handleOverlayClick
    );
    this.addEventListener('click', this.handleCloseButtonClick);
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('click', this.handleExternalTriggerClick);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.querySelector(SELECTORS.COUPON_MODAL.OVERLAY)?.removeEventListener(
      'click',
      this.handleOverlayClick
    );
    this.removeEventListener('click', this.handleCloseButtonClick);
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('click', this.handleExternalTriggerClick);
  }

  private handleOpen = (): void => {
    const overlay = this.querySelector<HTMLElement>(SELECTORS.COUPON_MODAL.OVERLAY);
    if (!overlay) return;

    this.previouslyFocusedElement = document.activeElement as HTMLElement;
    overlay.classList.remove(HIDDEN_CLASS);
    overlay.classList.add(FLEX_CLASS);
    overlay.removeAttribute('aria-hidden');

    // overlay 全体（パネル + パネル外の close ボタン等）から最初の focusable 要素を取得
    const firstFocusable = overlay.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    firstFocusable?.focus();
  };

  private handleClose = (): void => {
    const overlay = this.querySelector<HTMLElement>(SELECTORS.COUPON_MODAL.OVERLAY);
    if (!overlay) return;

    overlay.classList.add(HIDDEN_CLASS);
    overlay.classList.remove(FLEX_CLASS);
    overlay.setAttribute('aria-hidden', 'true');

    this.previouslyFocusedElement?.focus();
    this.previouslyFocusedElement = null;

    this.syncAppliedToExternalTriggers();
  };

  /**
   * モーダル内 kd-coupon-get が `data-coupon-get-applied` をボタンに付与した場合、
   * document 内のすべての `[data-coupon-trigger]` 要素にも同属性を伝播させる。
   * トリガーボタンが内部に `[data-coupon-get-label]` を持つ場合（v2-product-coupon）は
   * ラベルテキストを「取得済み」に更新 + disabled にする。
   * 単純なテキストボタン（v2-product-cart）は属性付与のみで挙動を維持する。
   *
   * 複数 kd-coupon-modal インスタンスが存在する場合は document 内で最初の 1 つだけが処理する。
   */
  private syncAppliedToExternalTriggers(): void {
    if (!this.isPrimaryInstance()) return;
    if (!this.querySelector('[data-coupon-get-applied]')) return;

    const triggers = document.querySelectorAll<HTMLButtonElement>(
      SELECTORS.COUPON_MODAL.EXTERNAL_TRIGGER
    );

    triggers.forEach((trigger) => {
      if (trigger.hasAttribute('data-coupon-get-applied')) return;
      trigger.setAttribute('data-coupon-get-applied', '');

      const label = trigger.querySelector<HTMLElement>(SELECTORS.COUPON.GET_LABEL);
      if (label) {
        label.textContent = LABEL_APPLIED;
        trigger.disabled = true;
      }
    });
  }

  private handleCloseButtonClick = (e: Event): void => {
    const target = e.target as HTMLElement;
    if (target.closest(SELECTORS.COUPON_MODAL.CLOSE)) {
      this.handleClose();
    }
  };

  private handleOverlayClick = (e: Event): void => {
    if (e.target === this.querySelector(SELECTORS.COUPON_MODAL.OVERLAY)) {
      this.handleClose();
    }
  };

  private handleKeyDown = (e: KeyboardEvent): void => {
    const overlay = this.querySelector<HTMLElement>(SELECTORS.COUPON_MODAL.OVERLAY);
    if (!overlay || overlay.classList.contains(HIDDEN_CLASS)) return;

    if (e.key === 'Escape') {
      this.handleClose();
    } else if (e.key === 'Tab') {
      this.trapFocus(e);
    }
  };

  private trapFocus = (e: KeyboardEvent): void => {
    // パネル外の close ボタンも含めるため overlay 全体を focus trap の境界とする
    const overlay = this.querySelector<HTMLElement>(SELECTORS.COUPON_MODAL.OVERLAY);
    if (!overlay) return;

    const focusable = Array.from(
      overlay.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
    ).filter((el) => !el.closest('[hidden]'));
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  /**
   * kd-coupon-modal 要素の外側にあるトリガーボタン（例: v2-product-cart の
   * `[data-coupon-trigger]` ボタン）からモーダルを開くためのハンドラ。
   * document 全体を監視する。
   *
   * 複数 kd-coupon-modal インスタンスが存在する場合、すべてが open してしまうのを防ぐため、
   * document 内で最初の 1 つだけが応答する（後続インスタンスは no-op）。
   */
  private handleExternalTriggerClick = (e: Event): void => {
    if (!this.isPrimaryInstance()) return;

    const target = e.target as HTMLElement;
    if (!target) return;

    const trigger = target.closest(SELECTORS.COUPON_MODAL.EXTERNAL_TRIGGER);
    if (!trigger) return;

    this.handleOpen();
  };

  /**
   * 自身が document 内で最初の kd-coupon-modal インスタンスか判定する。
   * 複数インスタンス存在時の二重発火・二重 sync を防ぐためのガード。
   */
  private isPrimaryInstance(): boolean {
    return document.querySelector('kd-coupon-modal') === this;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-coupon-modal': KdCouponModal;
  }
}
